import Drawer from '@/components/Drawer';
import PrettyDate from '@/components/PrettyDate';
import PERMISSIONS from '@/constants/permissions';
import {
  fetchEmailTemplateHistoryRequest,
  restoreEmailTemplateVersionRequest,
} from '@/services/api/emailCenter';
import { EmailTemplateVersionModel } from '@/typings/api/emailCenter';
import { useHasAccess } from '@/utils/hooks';
import { F, getIntlTranslation, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Alert, Button, Empty, Popconfirm, Space, Spin, Tag } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'umi';

import styles from './TemplateHistoryDrawer.less';

interface Props {
  /** Template uuid. */
  uuid: string;
  templateName: string;
  onClose: () => void;
  /** Restored — the parent must refetch; the template's fields have changed. */
  onRestored: () => void;
}

/**
 * A template's version history, stacked over the editor drawer.
 *
 * **What a row means.** Each entry holds the values that were *replaced*, so it
 * describes the state you would go back to by restoring it — not the change that
 * was made. `changed_at` is when those values stopped being current. That is why
 * the newest entry is not the template as it stands now: the current values live
 * on the template itself, and only enter this list once something replaces them.
 *
 * **Restoring is not destructive.** The values being replaced are snapshotted
 * first, so a restore is itself undoable and appears as a new entry. The confirm
 * copy says so, because "restore" otherwise reads as something you cannot walk
 * back.
 *
 * Bodies are deliberately absent from the API's list response — 100 of them
 * would be megabytes — so a row shows `body_size` instead of a preview. Seeing
 * the actual content means restoring it (and, if wrong, restoring back).
 */
const TemplateHistoryDrawer: React.FC<Props> = ({
  uuid,
  templateName,
  onClose,
  onRestored,
}) => {
  const intl = useIntl();
  const canRestore = useHasAccess([PERMISSIONS.EmailTemplate.PUBLISH]);
  const [versions, setVersions] = useState<EmailTemplateVersionModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchEmailTemplateHistoryRequest({ params: { uuid } });
    setLoading(false);

    processAPIResponseNow(
      result,
      {
        onSuccess: (data) => {
          setError(null);
          setVersions(Array.isArray(data) ? data : []);
        },
        onError: () =>
          setError(
            getIntlTranslation(intl, {
              id: 'pages.emailCenter.templates.historyLoadFailed',
              defaultMessage: 'The version history could not be loaded.',
            }),
          ),
      },
      // Unwraps the `{ data, message, success }` envelope; without it `onSuccess`
      // receives the envelope and the list reads as empty.
      'data',
    );
  }, [uuid, intl]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = useCallback(
    async (historyId: number) => {
      setRestoringId(historyId);
      const result = await restoreEmailTemplateVersionRequest({
        params: { uuid, historyId },
      });
      setRestoringId(null);

      processAPIResponseNow(result, {
        showSuccessMessage: true,
        // Without an entity the shared template renders a bare "geändert".
        successEntity: INTL_ENTITY.EMAIL_TEMPLATE,
        onSuccess: () => {
          onRestored();
          onClose();
        },
      });
    },
    [uuid, onRestored, onClose],
  );

  return (
    <Drawer
      width={720}
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        <Space size={8}>
          <F
            id="pages.emailCenter.templates.historyTitle"
            defaultMessage="Version history"
          />
          <span className={styles.subject}>{templateName}</span>
        </Space>
      }
    >
      {error && (
        <Alert className={styles.error} type="error" showIcon message={error} />
      )}

      <p className={styles.hint}>
        <F
          id="pages.emailCenter.templates.historyHint"
          defaultMessage="Each entry is a version this template was changed away from. Restoring one brings those values back — the current version is kept, so you can always go forward again."
        />
      </p>

      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : versions.length === 0 ? (
        <Empty
          description={
            <F
              id="pages.emailCenter.templates.historyEmpty"
              defaultMessage="No earlier versions yet. One is kept every time the content changes."
            />
          }
        />
      ) : (
        <div className={styles.list}>
          {versions.map((version) => (
            <div key={version.id} className={styles.row}>
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>
                  {version.subject || (
                    <span className={styles.muted}>
                      <F
                        id="pages.emailCenter.templates.historyNoSubject"
                        defaultMessage="(no subject)"
                      />
                    </span>
                  )}
                </div>
                <div className={styles.rowMeta}>
                  {version.changed_at && (
                    <span className={styles.metaItem}>
                      <PrettyDate date={version.changed_at} />
                    </span>
                  )}
                  {version.changed_by && (
                    <span className={styles.metaItem}>
                      {version.changed_by.name}
                    </span>
                  )}
                  <Tag className={styles.sizeTag}>
                    <F
                      id="pages.emailCenter.templates.historyBodySize"
                      defaultMessage="{kb} KB body"
                      values={{
                        kb: Math.max(1, Math.round(version.body_size / 1024)),
                      }}
                    />
                  </Tag>
                </div>
              </div>

              {/* Restoring replaces what is being sent — publish permission
                (backend gates the restore endpoint the same way). */}
              {canRestore && (
                <Popconfirm
                  title={
                    <F
                      id="pages.emailCenter.templates.historyRestoreConfirm"
                      defaultMessage="Restore this version? The current one is kept in the history, so this can be undone."
                    />
                  }
                  onConfirm={() => handleRestore(version.id)}
                >
                  <Button size="small" loading={restoringId === version.id}>
                    <F
                      id="pages.emailCenter.templates.historyRestore"
                      defaultMessage="Restore"
                    />
                  </Button>
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};

export default TemplateHistoryDrawer;
